import { Button, Typography } from '@material-tailwind/react';
import FloatingBarFrame from './FloatingBarFrame';
import {
  FreeTrialStatusItem,
  FREE_TRIAL_PROGRESS_STEPS,
  FreeTrialStepProgress,
  TOTAL_FREE_TRIAL_STEP,
} from '../../../constants/types/free-trial/type';
import { formatDDay } from '../../../api/utils/util-func';
import { useNavigate } from 'react-router-dom';

interface ActiveFreeTrialFloatingBarProps {
  isVisible: boolean;
  isScrolling: boolean;
  freeTrialStatus: FreeTrialStatusItem;
}

const getFreeTrialCurrentStep = (
  stepProgress: FreeTrialStepProgress | null,
) => {
  if (!stepProgress) {
    return FREE_TRIAL_PROGRESS_STEPS[0];
  }

  const currentStep = [...FREE_TRIAL_PROGRESS_STEPS]
    .reverse()
    .find(({ progressKey }) => Boolean(stepProgress[progressKey]));

  return currentStep ?? FREE_TRIAL_PROGRESS_STEPS[0];
};

const ActiveFreeTrialFloatingBar = ({
  isVisible,
  isScrolling,
  freeTrialStatus,
}: ActiveFreeTrialFloatingBarProps) => {
  const navigate = useNavigate();

  const onClickContinue = () => {
    sessionStorage.setItem('freeTrialLinkToken', freeTrialStatus.linkToken);

    navigate('/free-trial/lesson');
  };

  const { step: currentStep, label: stepLabel } = getFreeTrialCurrentStep(
    freeTrialStatus.stepProgress,
  );

  const progress = (currentStep / TOTAL_FREE_TRIAL_STEP) * 100;

  const floatingBarStateClass = isScrolling
    ? 'gap-2 rounded-[18px] p-1'
    : 'gap-3 rounded-[20px] p-2 lg:gap-4 lg:px-4 lg:py-2.5';

  const progressStateClass = isScrolling
    ? 'pl-2 lg:w-[82px] lg:flex-none'
    : 'pl-2 lg:w-[250px] lg:flex-none';

  const continueButtonStateClass = isScrolling
    ? 'h-10 px-4 text-[12px]'
    : 'h-[60px] px-6 text-[14px] lg:h-14 lg:px-6 lg:text-[14px]';

  return (
    <FloatingBarFrame
      isVisible={isVisible}
      isScrolling={isScrolling}
      compactWidthClass="w-[185px]"
    >
      <div
        className={`flex w-full items-center border border-[#E5BE00] bg-[#FFD928] shadow-[0_8px_24px_rgba(88,57,180,0.18)] transition-all duration-300 ease-out ${floatingBarStateClass} lg:w-max lg:rounded-full`}
      >
        {/* 진행 상태 */}
        <div
          className={`min-w-0 flex-1 transition-[width,padding] duration-300 ease-out lg:flex-none ${progressStateClass}`}
        >
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Typography
              className={`font-extrabold leading-none text-gray-900 ${
                isScrolling ? 'text-[10px]' : 'text-[13px] lg:text-[12px]'
              }`}
            >
              {currentStep}/{TOTAL_FREE_TRIAL_STEP}단계
            </Typography>

            {!isScrolling && (
              <>
                <span className="text-[10px] text-gray-800">·</span>

                <Typography className="truncate text-[12px] font-bold leading-none text-gray-800 lg:text-[12px]">
                  {stepLabel}
                </Typography>
              </>
            )}

            <span className="text-[10px] text-gray-800">·</span>

            <Typography
              className={`shrink-0 font-extrabold leading-none text-red-500 ${
                isScrolling ? 'text-[10px]' : 'text-[13px] lg:text-[12px]'
              }`}
            >
              {formatDDay(freeTrialStatus.expireAt)}
            </Typography>
          </div>

          {!isScrolling && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#ECE8FA]">
              <div
                className="h-full rounded-full bg-[#6737F4] transition-[width] duration-500"
                style={{
                  width: `${progress}%`,
                }}
              />
            </div>
          )}
        </div>

        <Button
          ripple={false}
          onClick={onClickContinue}
          className={`group relative shrink-0 overflow-visible rounded-[18px] bg-[#6737F4] py-0 font-bold normal-case text-white shadow-none transition-all duration-300 hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none lg:rounded-full ${continueButtonStateClass}`}
        >
          {!isScrolling && (
            <>
              {/* ring pulse */}
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-[3px] block animate-[free-trial-ring_2.8s_ease-in-out_infinite] rounded-[18px] border-2 border-[#A98EFF]/50 lg:rounded-full"
              />

              {/* shine */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 block overflow-hidden rounded-[18px] lg:rounded-full"
              >
                <span className="absolute -inset-y-4 -left-16 w-10 rotate-[18deg] animate-[free-trial-shine_2.6s_ease-in-out_infinite] bg-white/50 blur-md" />
              </span>
            </>
          )}

          <span className="relative z-10 whitespace-nowrap">이어서 하기</span>
        </Button>
      </div>
    </FloatingBarFrame>
  );
};

export default ActiveFreeTrialFloatingBar;
